import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useRevenueAnalytics } from "@/hooks/useAnalytics";

interface ChartExportButtonProps {
  chartId?: string;
  filename?: string;
  className?: string;
}

const ChartExportButton = ({ 
  chartId,
  filename = "chart",
  className = ""
}: ChartExportButtonProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const chartRef = useRef<HTMLElement | null>(null);
  const { trackExport } = useRevenueAnalytics();

  useEffect(() => {
    if (chartId) {
      chartRef.current = document.getElementById(chartId);
    }
  }, [chartId]);

  const addWatermarkToImage = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
    // Subtle attribution watermark (everything on Wage Atlas is free)
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = '#6b7280';
    ctx.font = '14px Arial';
    ctx.textAlign = 'right';
    ctx.fillText('Generated by WageAtlas.com', canvas.width - 20, canvas.height - 20);
    ctx.restore();
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    // Track export attempt
    trackExport('png', 'chart', false);
    
    try {
      const chartElement = chartRef.current || document.querySelector('.plotly-graph-div');
      
      if (!chartElement) {
        throw new Error("Chart not found");
      }

      // Use Plotly's built-in export functionality
      const plotlyDiv = chartElement as unknown;
      if (window.Plotly && plotlyDiv._fullLayout) {
        // For Plotly charts, we need to handle watermarking differently
        const exportConfig = {
          format: 'png' as const,
          width: 1200,
          height: 800,
          filename
        };

        await window.Plotly.downloadImage(plotlyDiv, exportConfig);
        
        toast.success("Chart exported successfully!");
      } else {
        // Fallback to canvas-based export
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          canvas.width = 1200;
          canvas.height = 800;
          
          // Create background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Add chart content placeholder
          ctx.fillStyle = '#333333';
          ctx.font = '24px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('Chart Export', canvas.width / 2, canvas.height / 2);
          
          // Add watermark for free users
          addWatermarkToImage(canvas, ctx);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${filename}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              
              toast.success("Chart exported!");
            }
          });
        }
      }

    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export chart. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      onClick={handleExport}
      disabled={isExporting}
      variant="outline"
      size="sm"
      aria-label="Export Chart"
      className={`shrink-0 ${className}`}
    >
      {isExporting ? (
        <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
      ) : (
        <Download className="h-4 w-4 sm:mr-2" />
      )}
      {isExporting ? (
        <span className="hidden sm:inline">Exporting...</span>
      ) : (
        <span className="hidden sm:inline">Export Chart</span>
      )}
    </Button>
  );
};

export default ChartExportButton;